import React from 'react';
import { Moment } from 'moment';
import cn from 'classnames';
import {
  GetJobsiteFormSubmissionsOrderByField,
  WorkerResponseFilter,
} from 'apollo/generated/client-operations';
import {
  AvatarCell,
  AvatarCellComponentProps,
  AvatarSkeleton,
  ChipsCell,
  ChipsCellComponentProps,
  ChipsSkeleton,
  DropdownButtonCell,
  DropdownButtonCellComponentProps,
  DropdownButtonSkeleton,
  DropdownFilterOption,
  FilterItems,
  getFixedWidthFaIcon,
  MenuItemProps,
} from '@odin-labs/components';
import { AuthUser, to } from 'acl';
import { Jobsite } from 'graphql/client/useAvailableJobsites';
import {
  ensureNonEmptyItems,
  getInitialsForUser,
  getUserFullName,
} from 'utils';
import { getPrettyFormattedDateTimeWithTz } from 'utils/dates';
import { getDeveloperOptionsFromJobsites } from 'utils/filters';
import {
  faEdit,
  faFileChartLine,
  faTrash,
  UserHardHatIcon,
} from 'components/icons';
import { getJobsitesOptions } from 'containers/users/modals/utils';
import { CustomFormColumnsOptions } from 'containers/jobsiteFormSubmission/types';
import { formSelectOptions } from 'containers/jobsiteFormSubmission/common';
import {
  DefaultExtraData,
  FormSubmissionColumn,
  FormSubmission,
  FormSubmissionFilters,
  FormSubmissionFiltersOptions,
  TypedFormSubmission,
  FormColumnName,
} from './types';

const FileChartLineIcon = getFixedWidthFaIcon({ icon: faFileChartLine });
const EditIcon = getFixedWidthFaIcon({ icon: faEdit });
const TrashIcon = getFixedWidthFaIcon({ icon: faTrash });

const classes = {
  manPowerContainer: cn('odin-flex odin-items-center odin-gap-x-1.5'),
  manPowerIcon: cn('odin-text-base odin-text-odin-primary'),
  manPowerText: cn(
    'odin-text-gray-900 odin-text-sm odin-leading-5 odin-font-normal',
  ),
};

export function ManpowerGatewayCell({
  value,
}: {
  value: string;
}): React.ReactElement {
  return (
    <div className={classes.manPowerContainer}>
      <UserHardHatIcon className={classes.manPowerIcon} />
      <span className={classes.manPowerText}>{value}</span>
    </div>
  );
}

type GetAvatarColumnArgs<
  TExtraData extends DefaultExtraData = DefaultExtraData,
> = Omit<
  FormSubmissionColumn<TExtraData>,
  'Cell' | 'CellLoading' | 'componentProps'
> & {
  componentProps: (
    formSubmission: TypedFormSubmission<TExtraData>,
  ) => AvatarCellComponentProps;
};

export function getAvatarColumn<
  TExtraData extends DefaultExtraData = DefaultExtraData,
>(args: GetAvatarColumnArgs<TExtraData>): FormSubmissionColumn<TExtraData> {
  return {
    Cell: AvatarCell,
    CellLoading: (): React.ReactElement => (
      <AvatarSkeleton size={['md', 'xl:xs']} />
    ),
    ...(args as FormSubmissionColumn<TExtraData>),
  };
}

export const getColumns = ({
  displayedByForm,
  extraColumns = [],
  columnsOptions = {},
  user,
  navigateToFormSubmissionPrintPage,
  navigateToFormSubmissionEditPage,
  openRemoveFormSubmissionModal,
}: {
  displayedByForm: boolean;
  extraColumns: FormSubmissionColumn[];
  columnsOptions: CustomFormColumnsOptions;
  user: AuthUser;
  navigateToFormSubmissionPrintPage: (formSubmission: FormSubmission) => void;
  navigateToFormSubmissionEditPage: (formSubmission: FormSubmission) => void;
  openRemoveFormSubmissionModal: (formSubmission: FormSubmission) => void;
}): FormSubmissionColumn[] => {
  const canEditFormSubmissions = user.isAllowed(to.editFormSubmissions);
  const canRemoveFormSubmissions = user.isAllowed(to.removeFormSubmissions);

  const { hideJobsite, hideCreatedBy } = columnsOptions;

  return ensureNonEmptyItems<FormSubmissionColumn>([
    !hideJobsite && {
      id: FormColumnName.JobsiteContractor,
      Header: 'Jobsite / Contractor',
      accessor: (): string[] => [],
      Cell: ChipsCell,
      CellLoading: ChipsSkeleton,
      componentProps: (
        formSubmission: FormSubmission,
      ): ChipsCellComponentProps => ({
        chips: [
          {
            text: formSubmission?.jobsiteForm.jobsite.name,
            secondaryText: formSubmission.jobsiteContractors.edges.find(
              ({ node: jsfC }) => jsfC.associationType === 'assignment',
            )?.node.jobsiteContractor.contractor.organization.name,
            withDot: false,
            // isActive: formSubmission.jobsiteWorker?.currentAccess.isAllowed,
          },
        ],
      }),
    },
    ...extraColumns,
    !hideCreatedBy && {
      id: FormColumnName.CreatedBy,
      Header: 'Created by',
      accessor: (formSubmission: FormSubmission): string =>
        getUserFullName(formSubmission.objectHistory.createdBy),
      Cell: AvatarCell,
      CellLoading: (): React.ReactElement => (
        <AvatarSkeleton size={['md', 'xl:xs']} />
      ),
      componentProps: (
        formSubmission: FormSubmission,
      ): AvatarCellComponentProps => ({
        src: formSubmission.objectHistory.createdBy.worker.profilePictureCropped
          ?.downloadUrl,
        placeholder: getInitialsForUser(formSubmission.objectHistory.createdBy),
        objectFit: 'cover',
        size: ['md', 'xl:xs'],
        detail: getPrettyFormattedDateTimeWithTz(
          formSubmission.objectHistory.createdAt,
          formSubmission.timeZone,
          true,
        ),
      }),
    },
    {
      id: FormColumnName.Actions,
      Header: '',
      Cell: DropdownButtonCell,
      CellLoading: (): React.ReactElement => (
        <DropdownButtonSkeleton cellAlignment="right" />
      ),
      componentProps: (
        formSubmission: FormSubmission,
      ): DropdownButtonCellComponentProps => {
        const { form } = formSubmission.jobsiteForm;
        return {
          cellAlignment: 'right',
          menuItems: ensureNonEmptyItems<MenuItemProps>([
            form.allowPrintView && {
              icon: FileChartLineIcon,
              text: 'View',
              theme: 'secondary',
              onClick: (): void =>
                navigateToFormSubmissionPrintPage(formSubmission),
            },
            form.allowEdit &&
              canEditFormSubmissions && {
                icon: EditIcon,
                text: 'Edit',
                theme: 'secondary',
                onClick: (): void =>
                  navigateToFormSubmissionEditPage(formSubmission),
              },
            form.allowDelete &&
              canRemoveFormSubmissions &&
              displayedByForm && {
                icon: TrashIcon,
                text: 'Delete',
                theme: 'danger',
                onClick: (): void =>
                  openRemoveFormSubmissionModal(formSubmission),
              },
          ]),
        };
      },
    },
  ]);
};

export const responseOptions: DropdownFilterOption[] = [
  { value: WorkerResponseFilter.Yes, label: 'Injured' },
  { value: WorkerResponseFilter.No, label: 'Not injured' },
  { value: WorkerResponseFilter.Pending, label: 'Awaiting response' },
];

const defaultFilterKeys: Array<keyof FormSubmissionFilters> = [
  'dateRange',
  'developerIds',
  'jobsiteIds',
  'contractorIds',
  'createdByIds',
  'hasAttachments',
];

const MIN_ITEMS_TO_SEARCH = 15;

export const getFilterItems = ({
  filterKeys = defaultFilterKeys,
  startDate,
  endDate,
  jobsites,
  filtersOptions,
  developerIds,
  jobsiteIds,
  contractorIds,
  formIds,
  createdByIds,
  hasAttachments,
  workerIds,
  responses,
  trainings,
  filterHeaders,
}: {
  filterKeys: Array<keyof FormSubmissionFilters>;
  startDate: Moment | string;
  endDate: Moment | string;
  jobsites: Jobsite[];
  filtersOptions: FormSubmissionFiltersOptions;
  developerIds: string[];
  jobsiteIds: string[];
  contractorIds: string[];
  formIds: string[];
  createdByIds: string[];
  hasAttachments: boolean;
  workerIds: string[];
  responses: string[];
  trainings: string[];
  filterHeaders?: Partial<Record<keyof FormSubmissionFilters, string>>;
}): FilterItems<FormSubmissionFilters> => {
  const result: FilterItems<Required<FormSubmissionFilters>> = {
    dateRange: {
      header: 'Date',
      type: 'datePicker',
      defaultValue: { startDate, endDate },
      layout: cn('odin-col-span-full'),
      componentProps: {
        isRange: true,
      },
    },
    developerIds: {
      header: 'Client',
      type: 'dropdown',
      defaultValue: developerIds,
      componentProps: {
        options: getDeveloperOptionsFromJobsites(jobsites),
        allowSearch: false,
      },
    },
    jobsiteIds: {
      header: 'Jobsite',
      type: 'dropdown',
      defaultValue: jobsiteIds,
      componentProps: {
        options: getJobsitesOptions(jobsites),
        allowSearch: jobsites?.length > MIN_ITEMS_TO_SEARCH,
      },
    },
    contractorIds: {
      header: 'Contractor',
      type: 'dropdown',
      defaultValue: contractorIds,
      componentProps: {
        options: filtersOptions?.contractors,
        allowSearch: filtersOptions?.contractors?.length > MIN_ITEMS_TO_SEARCH,
      },
    },
    workerIds: {
      header: 'Worker',
      type: 'dropdown',
      defaultValue: workerIds,
      componentProps: {
        options: filtersOptions?.workers,
        allowSearch: filtersOptions?.workers?.length > MIN_ITEMS_TO_SEARCH,
      },
    },
    responses: {
      header: 'Response',
      type: 'dropdown',
      defaultValue: responses,
      componentProps: {
        options: responseOptions,
        allowSearch: false,
      },
    },
    trainings: {
      header: 'Training',
      type: 'dropdown',
      defaultValue: trainings,
      componentProps: {
        options: formSelectOptions.training,
        allowSearch: false,
      },
    },
    formIds: {
      header: 'Log',
      type: 'dropdown',
      defaultValue: formIds,
      componentProps: {
        options: filtersOptions?.forms,
        allowSearch: false,
      },
    },
    createdByIds: {
      header: 'Created by',
      type: 'dropdown',
      defaultValue: createdByIds,
      componentProps: {
        options: filtersOptions?.createdByUsers,
        allowSearch:
          filtersOptions?.createdByUsers?.length > MIN_ITEMS_TO_SEARCH,
      },
    },
    hasAttachments: {
      header: 'Has Attachment(s)',
      type: 'checkbox',
      defaultValue: hasAttachments,
    },
  };

  // Remove filter items that are not included in filters
  Object.keys(result).forEach((key) => {
    if (!filterKeys.includes(key as keyof FormSubmissionFilters)) {
      delete result[key as keyof FormSubmissionFilters];
    }
  });

  // Replace the default headers with the ones set by the form
  Object.entries(filterHeaders ?? {}).forEach(([key, header]) => {
    const item = result[key as keyof FormSubmissionFilters];
    if (item) {
      item.header = header;
    }
  });
  return result;
};

export const orderByFields: Record<
  string,
  GetJobsiteFormSubmissionsOrderByField
> = {
  [FormColumnName.JobsiteContractor]:
    GetJobsiteFormSubmissionsOrderByField.Name,
  [FormColumnName.Date]: GetJobsiteFormSubmissionsOrderByField.Date,
  [FormColumnName.Manpower]: GetJobsiteFormSubmissionsOrderByField.Manpower,
  [FormColumnName.StartTime]: GetJobsiteFormSubmissionsOrderByField.StartTime,
  [FormColumnName.EndTime]: GetJobsiteFormSubmissionsOrderByField.EndTime,
  [FormColumnName.CreatedBy]: GetJobsiteFormSubmissionsOrderByField.CreatedBy,
  [FormColumnName.Building]: GetJobsiteFormSubmissionsOrderByField.Building,
  [FormColumnName.Incident]: GetJobsiteFormSubmissionsOrderByField.Incident,
  [FormColumnName.ReportedBy]: GetJobsiteFormSubmissionsOrderByField.ReportedBy,
  [FormColumnName.ObservationType]:
    GetJobsiteFormSubmissionsOrderByField.ObservationType,
  [FormColumnName.WorkersCount]:
    GetJobsiteFormSubmissionsOrderByField.WorkersCount,
  [FormColumnName.InspectedBy]:
    GetJobsiteFormSubmissionsOrderByField.InspectedBy,
  [FormColumnName.InspectionType]:
    GetJobsiteFormSubmissionsOrderByField.InspectionType,
  [FormColumnName.ExtinguisherNo]:
    GetJobsiteFormSubmissionsOrderByField.ExtinguisherNo,
  [FormColumnName.Level]: GetJobsiteFormSubmissionsOrderByField.Level,
  [FormColumnName.TimeIn]: GetJobsiteFormSubmissionsOrderByField.StartTime,
  [FormColumnName.TempBadge]: GetJobsiteFormSubmissionsOrderByField.TempBadge,
  [FormColumnName.TempBadgeExpiration]:
    GetJobsiteFormSubmissionsOrderByField.TempBadgeExpiration,
  [FormColumnName.Visitor]: GetJobsiteFormSubmissionsOrderByField.Visitor,
  [FormColumnName.AssignedTo]: GetJobsiteFormSubmissionsOrderByField.AssignedTo,
  [FormColumnName.Response]: GetJobsiteFormSubmissionsOrderByField.Response,
  [FormColumnName.RespondedAt]:
    GetJobsiteFormSubmissionsOrderByField.RespondedAt,
};
